<template>
  <div class="activity-redirect">
    <van-loading size="24px" vertical color="#333">加载中...</van-loading>
  </div>
</template>

<script>
import { getSignStoreActivityDetailByCode, h5AnonymousSilentLogin, h5ThirdPartSilentLogin } from '@/api/api'
import { ensureSignstoreH5Uid } from '@/utils/signstore-h5-uid'
import {
  clearSignstoreH5AuthSession,
  clearSignstoreH5UserInfo,
  ensureSignstoreAnonymousLoggedIn,
  isSignstoreUnauthorizedResponse,
  isThirdPartUidLoginRejectedResponse,
  getSignstoreUnauthorizedMessage,
  markSignstoreH5Auth,
  mergeActivityEntryQuery,
  resolveActivityThirdPartUid,
  shouldIgnoreUrlUidForActivity,
  signstoreActivityEntryAuth,
  signstoreAnonymousFreshLoginForActivityEntry,
  stripIgnoredUidFromBrowserUrl,
  SIGNSTORE_H5_USER_INFO_KEY
} from '@/utils/signstore-h5-auth'
import { Toast } from 'vant'

/**
 * 活动跳转页（与小程序 activityRedirect 逻辑对齐）
 * - 带 query.uid：第三方静默登录后进活动（部分活动编码见 SIGNSTORE_IGNORE_URL_UID_ACTIVITY_CODES 会忽略 uid）
 * - 不带 uid：H5 匿名自动注册（h5_uuid + 活动页 getOrCreateUserRecordH5），与历史 H5 链路一致
 */
export default {
  name: 'ActivityRedirect',
  data() {
    return {
      activityCode: null,
      thirdPartUid: null,
      h5Uid: null,
      launchPageKey: '',
      useThirdPartLogin: false,
      /** 非法访问后仅重试一次，避免死循环 */
      _unauthorizedReentryAttempted: false
    }
  },
  mounted() {
    const query = mergeActivityEntryQuery(this.$route)
    let activityCode = query.activity_code || null
    if (!activityCode && query.scene) {
      activityCode = decodeURIComponent(query.scene)
    }
    if (activityCode) {
      stripIgnoredUidFromBrowserUrl(activityCode, this.$router)
    }
    const uidRaw = query.uid != null ? String(query.uid).trim() : ''
    const effectiveUid = resolveActivityThirdPartUid(activityCode, uidRaw)
    if (effectiveUid) {
      this.thirdPartUid = effectiveUid
      this.useThirdPartLogin = true
    } else {
      this.h5Uid = ensureSignstoreH5Uid(query.h5_uid)
      this.useThirdPartLogin = false
    }

    if (activityCode) {
      this.activityCode = activityCode
      if (query.pageKey != null && String(query.pageKey).trim() !== '') {
        this.launchPageKey = String(query.pageKey).trim()
      }
      this.bootstrap()
    } else {
      Toast('活动编码不存在')
      setTimeout(() => {
        this.$router.replace('/index')
      }, 1500)
    }
  },
  methods: {
    persistThirdPartLoginPayload(loginRes) {
      const { token, userInfo } = loginRes.data
      try {
        window.localStorage.setItem('token', token)
        if (userInfo) {
          window.localStorage.setItem(SIGNSTORE_H5_USER_INFO_KEY, JSON.stringify(userInfo))
        }
        markSignstoreH5Auth('thirdPart', this.thirdPartUid)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('写入本地缓存失败', e)
      }
    },
    /** 进页：清旧 token 后强制匿名 silentLogin */
    async ensureAnonymousSilentLogin() {
      const auth = await signstoreAnonymousFreshLoginForActivityEntry(
        this.h5Uid || (this.$route.query && this.$route.query.h5_uid),
        h5AnonymousSilentLogin
      )
      this.h5Uid = auth.h5Uid
      return true
    },
    relaunchSelfAsNewAnonymous() {
      const query = {
        activity_code: this.activityCode
      }
      if (this.launchPageKey) {
        query.pageKey = this.launchPageKey
      }
      if (this.h5Uid) {
        query.h5_uid = this.h5Uid
      }
      this.$router.replace({
        path: '/loading/activityRedirect',
        query
      })
    },
    /** 401 / 非法访问：清鉴权缓存后按新用户（匿名换新 h5_uid）或第三方重新静默登录 */
    async recoverFromUnauthorizedResponse(res) {
      if (!isSignstoreUnauthorizedResponse(res)) return false
      if (this._unauthorizedReentryAttempted) return false
      this._unauthorizedReentryAttempted = true

      if (this.useThirdPartLogin && !shouldIgnoreUrlUidForActivity(this.activityCode)) {
        clearSignstoreH5AuthSession()
        clearSignstoreH5UserInfo()
        const loginRes = await h5ThirdPartSilentLogin({
          uid: this.thirdPartUid,
          activityCode: this.activityCode
        })
        if (isThirdPartUidLoginRejectedResponse(loginRes) || shouldIgnoreUrlUidForActivity(this.activityCode)) {
          this.useThirdPartLogin = false
          this.thirdPartUid = null
          const auth = await ensureSignstoreAnonymousLoggedIn(this.h5Uid, h5AnonymousSilentLogin)
          this.h5Uid = auth.h5Uid
          return true
        }
        if (!loginRes || loginRes.status !== 200 || !loginRes.data || !loginRes.data.token) {
          throw new Error((loginRes && loginRes.msg) || '静默登录失败')
        }
        this.persistThirdPartLoginPayload(loginRes)
        return true
      }

      this.useThirdPartLogin = false
      try {
        const auth = await ensureSignstoreAnonymousLoggedIn(this.h5Uid, h5AnonymousSilentLogin)
        this.h5Uid = auth.h5Uid
        return true
      } catch (e) {
        throw e
      }
    },
    async bootstrap() {
      try {
        const query = mergeActivityEntryQuery(this.$route)
        const auth = await signstoreActivityEntryAuth({
          activityCode: this.activityCode,
          uidRaw: query.uid,
          h5UidFromQuery: this.h5Uid || query.h5_uid,
          thirdPartLoginFn: h5ThirdPartSilentLogin,
          anonymousLoginFn: h5AnonymousSilentLogin,
          freshAnonymous: true
        })
        if (auth.mode === 'thirdPart' && auth.failed) {
          throw new Error((auth.loginRes && auth.loginRes.msg) || '静默登录失败')
        }
        if (auth.mode === 'thirdPart') {
          this.useThirdPartLogin = true
          this.thirdPartUid = auth.uid
          this.persistThirdPartLoginPayload(auth.loginRes)
        } else {
          this.useThirdPartLogin = false
          this.thirdPartUid = null
          this.h5Uid = auth.h5Uid
        }
        await this.loadActivityDetail()
      } catch (e) {
        const msg = (e && e.message) || (this.useThirdPartLogin ? '登录失败' : '加载失败')
        Toast(msg)
        if (!this.useThirdPartLogin && this.activityCode && /非法访问|未授权|未登录/i.test(msg)) {
          setTimeout(() => {
            this.relaunchSelfAsNewAnonymous()
          }, 800)
          return
        }
        setTimeout(() => {
          this.$router.replace('/index')
        }, 2000)
      }
    },
    /** 跳转 query：第三方 uid 或匿名 h5_uid */
    buildUserQuery() {
      if (shouldIgnoreUrlUidForActivity(this.activityCode)) {
        if (this.h5Uid) {
          return `h5_uid=${encodeURIComponent(this.h5Uid)}`
        }
        return ''
      }
      if (this.useThirdPartLogin && this.thirdPartUid) {
        return `uid=${encodeURIComponent(this.thirdPartUid)}`
      }
      if (this.h5Uid) {
        return `h5_uid=${encodeURIComponent(this.h5Uid)}`
      }
      return ''
    },
    async loadActivityDetail() {
      try {
        const params = { activityCode: this.activityCode }
        const res = await getSignStoreActivityDetailByCode(params)
        if (isSignstoreUnauthorizedResponse(res)) {
          const recovered = await this.recoverFromUnauthorizedResponse(res)
          if (recovered) {
            return this.loadActivityDetail()
          }
          this.relaunchSelfAsNewAnonymous()
          return
        }
        if (!res || res.status !== 200) {
          throw new Error((res && res.msg) || '获取活动详情失败')
        }
        const activityData = res.data
        if (!activityData) {
          throw new Error('活动数据为空')
        }

        const templateCode = activityData.templateCode || ''
        let pageConfig = activityData.pageConfig || {}
        if (activityData.pageConfigJson) {
          try {
            pageConfig =
              typeof activityData.pageConfigJson === 'string'
                ? JSON.parse(activityData.pageConfigJson)
                : activityData.pageConfigJson
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('解析 pageConfigJson 失败:', e)
          }
        }

        const rawIsShowIndex = pageConfig.isShowIndex
        const hideIndex =
          rawIsShowIndex === false ||
          rawIsShowIndex === 0 ||
          rawIsShowIndex === '0' ||
          rawIsShowIndex === 'false'

        const userQs = this.buildUserQuery()

        if (templateCode === 'ACT003' && hideIndex) {
          let h5Path = '/template/activity3-1'
          const qs = []
          if (activityData.id) qs.push(`id=${activityData.id}`)
          if (this.activityCode) qs.push(`activity_code=${encodeURIComponent(this.activityCode)}`)
          if (this.launchPageKey) qs.push(`pageKey=${encodeURIComponent(this.launchPageKey)}`)
          if (userQs) qs.push(userQs)
          if (qs.length > 0) {
            h5Path += `?${qs.join('&')}`
          }
          this.$router.replace(h5Path)
          return
        }

        if (!activityData.id || !activityData.miniProgramPath) {
          throw new Error('活动数据不完整')
        }
        const miniPath = (activityData.miniProgramPath || '').trim()
        let h5Path =
          miniPath ? ('/' + miniPath.replace(/^\//, '')).replace(/^\/pages\//, '/') : '/template/activity3'
        if (!h5Path.startsWith('/')) h5Path = '/' + h5Path
        const qs = [
          `id=${activityData.id}`,
          `activity_code=${encodeURIComponent(this.activityCode)}`
        ]
        if (userQs) qs.push(userQs)
        if (this.launchPageKey) {
          qs.push(`pageKey=${encodeURIComponent(this.launchPageKey)}`)
        }
        const redirectUrl = `${h5Path}?${qs.join('&')}`
        this.$router.replace(redirectUrl)
      } catch (error) {
        const msg = (error && error.message) || '加载活动失败'
        Toast(msg)
        if (!this.useThirdPartLogin && this.activityCode && /非法访问|未授权|未登录/i.test(msg)) {
          setTimeout(() => {
            this.relaunchSelfAsNewAnonymous()
          }, 800)
          return
        }
        setTimeout(() => {
          this.$router.replace('/index')
        }, 2000)
      }
    }
  }
}
</script>

<style scoped lang="less">
.activity-redirect {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: #f5f5f5;
}
</style>

